// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { LabelContents } from 'components/molecules/LabelContents';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createAxios } from 'ts/createAxios';
import {
  getFormatCurrentDate,
  getFormatCurrentDateTime,
  getFormatDateWithCurrentTimezone,
} from 'ts/formatDate';
import { DATE_FORMAT } from 'util/ConstantValues';
import {
  ByTenant,
  CollectHistory,
  CollectHistoryListResponse,
  EdiStatus,
} from 'util/Types';
import styles from './HistoryCollected.module.scss';
import EdiStatusTag from 'components/atoms/EdiStatusTag';
import { OperatorType } from 'util/Enums';
import { getCollectTenantMapper, normalizeNumber } from 'util/commons';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { getCollectHistoryCSVDataGroupedByTenant } from 'util/csvExportUtils';
import { handleError } from 'util/errorHandler';
import CustomPagination from 'components/atoms/CustomPagination';
import { getCollectDeliveries } from 'apis/collects';
import CollectDeliveriesDataModal from 'components/molecules/CollectDeliveriesDataModal';
import CustomCSVLink from 'components/atoms/CustomCsvLink';

export type Props = {
  id?: string;
};

export const HistoryCollected = () => {
  const { t } = useTranslation();
  const [collectedData, setCollectedData] = useState<CollectHistory[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });
  const [emissionDetailCollectId, setEmissionDetailCollectId] =
    useState<string>();
  const [exportCsvCollectData, setExportCsvCollectData] = useState<{
    id: string;
    targetIds: string[];
    byTenant: ByTenant;
    wasteUnitName: string | undefined;
  }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const csvRef = useRef<any>(null);

  const pageCount = Math.ceil(pageMeta.total / 200);

  const callApi = async (page = pageMeta.page) => {
    try {
      const res = await createAxios({
        prefix: 'v2',
      }).get<CollectHistoryListResponse>('/work-histories/collect/receives', {
        params: {
          page,
          take: 200,
        },
      });

      setCollectedData(res.data.deliveries);
      setPageMeta({
        page: res.data.page,
        total: res.data.total,
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    callApi(selectedItem.selected + 1);
  };

  const handleExportCSV = async (collect: CollectHistory) => {
    await getCollectDeliveries({
      deliveryIds: [collect.id],
      cursor: null,
      successCallback: (data) => {
        setExportCsvCollectData({
          id: collect.id,
          targetIds: data
            .map((item) => item.processingObjectId)
            .filter(Boolean),
          byTenant: getCollectTenantMapper(data)[collect.id] || {},
          wasteUnitName: data.find(
            ({ deliveryId }) => deliveryId === collect.id
          )?.wasteUnitName,
        });

        setTimeout(() => {
          csvRef.current?.link?.click();
          setExportCsvCollectData(undefined);
        }, 100);
      },
      failedCallback: () => {
        setExportCsvCollectData(undefined);
      },
    });
  };

  useEffect(() => {
    callApi();
  }, []);

  const renderContentList = () => {
    if (!collectedData.length) {
      return <div className={styles.noData}>{t('messages.M_008')}</div>;
    }

    return collectedData.map((collect, index) => {
      return (
        <div className={styles.historyCollectedCard} key={index}>
          <div className={styles.historyCollectedContentCol}>
            <LabelContents
              label={t('common_history_field.date_time')}
              contents={getFormatDateWithCurrentTimezone(
                collect.receivedDatetime,
                DATE_FORMAT.slaYMDHm
              )}
            />
            <LabelContents
              label={t('common_history_field.responsible_person')}
              contents={collect.userId + '(' + collect.userName + ')'}
            />
            <LabelContents
              label={t('common_history_field.place')}
              contents={collect.areaName}
            />
            <LabelContents
              label={t('common_history_field.material_name')}
              contents={collect.description}
            />
            <LabelContents
              label={t('common_history_field.item')}
              contents={collect.wasteUnitName}
            />
            <LabelContents
              label={t('common_history_field.weight')}
              contents={
                normalizeNumber({
                  value: collect.weight,
                }) + 'kg'
              }
            />
            <LabelContents
              label={t('common_history_field.label_manifest_number')}
              contents={collect.ediNumber}
              hideOnEmpty
            />
            <LabelContents
              label={t('common_history_field.label_edi_id')}
              hideOnEmpty
              contents={
                collect.receivedEdiStatus != null &&
                collect.receivedEdiStatus !== EdiStatus.None && (
                  <EdiStatusTag
                    status={collect.receivedEdiStatus}
                    operatorType={OperatorType.Collects}
                  />
                )
              }
            />
          </div>
          <div className={styles.historyCollectedActionCol}>
            <button
              onClick={() => setEmissionDetailCollectId(collect.id)}
              className={styles.viewButton}
            >
              {t('common.button.view_details')}
            </button>

            <div
              className={styles.download}
              onClick={() => handleExportCSV(collect)}
            >
              <IconDownload strokeColor="black" />
            </div>

            {exportCsvCollectData?.id === collect.id && (
              <CustomCSVLink
                className={styles.csvLink}
                ref={csvRef}
                filename={`${t(
                  'histories.csv_file_name_collect_by_waste_unit'
                )}_${getFormatCurrentDate()}`}
                data={getCollectHistoryCSVDataGroupedByTenant({
                  type: 'received',
                  data: collect,
                  targetIds: exportCsvCollectData.targetIds,
                  byTenant: exportCsvCollectData.byTenant,
                  exportDatetimeString: getFormatCurrentDateTime(),
                  wasteUnitName: exportCsvCollectData.wasteUnitName,
                  t,
                })}
              />
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.historyCollected}>
      <div className={styles.historyCollectedContent}>
        {renderContentList()}
      </div>

      <div className={styles.paginationContainer}>
        <CustomPagination
          pageCount={pageCount}
          onPageChange={handlePageClick}
          forcePage={pageMeta.page - 1}
        />
      </div>

      <CollectDeliveriesDataModal
        collectId={emissionDetailCollectId}
        onClose={() => {
          setEmissionDetailCollectId(undefined);
        }}
      />
    </div>
  );
};
